import { Trans, useLingui } from "@lingui/react/macro";
import React, { useCallback, useMemo } from "react";
import {
  BackgroundColor,
  Button,
  classForBackground,
  Icon,
  ImageRatio,
  ProductTypeColor,
  PromoBadges,
  ResponsiveSanityImage
} from "@silversea-ssc/ui-components-react";
import { CruiseCardModel } from "../types";
import * as styles from "./EmotionalCruiseCard.module.scss";
import { arrayIsNotEmpty } from "src/utils/checks";
import Dates from "../Dates/Dates";
import LowestPrice from "../Prices/LowestPrice";
import { generateTrackingContentParams } from "../utils";
import { useIsDesktop } from "src/utils/useIsTabletOrDesktop";
import CardTag from "components/CardTags/CardTag";
import CardTags from "components/CardTags/CardTags";
import { match, P } from "ts-pattern";
import clsx from "clsx";
import { nonBreakingSpaceCruiseName } from "src/utils/cruise";

export type EmotionalCruiseCardProps = CruiseCardModel & {
  backgroundColor: BackgroundColor;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    cruiseCard: CruiseCardModel
  ) => void;
};

const EmotionalCruiseCard: React.FC<
  React.PropsWithChildren<EmotionalCruiseCardProps>
> = ({ backgroundColor = "white", onClick, ...props }) => {
  const { t } = useLingui();
  const isDesktop = useIsDesktop();
  const isAntarcticaBridge = props.cruiseGroup == "AntarcticaBridge";
  const leadPriceFrom = props.leadPriceFrom;
  const label = `${props.departurePort?.name || ""} ${t`port-to-port`} ${props.arrivalPort?.name || ""}`;
  const bgClass = classForBackground(backgroundColor);

  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(e, props);
      }
    },
    [onClick, props]
  );

  const visibleDestinationName = useMemo(
    () =>
      match([!!props.showDestination, props.comboType])
        .with([true, P.any], () => props.destinationName)
        .with([false, "World"], [false, "Grand"], () => props.destinationName)
        .with([false, P.any], () => undefined)
        .exhaustive(),
    [props.showDestination, props.comboType, props.destinationName]
  );

  return (
    <ProductTypeColor
      productType={props.isExpedition ? "expedition" : "ocean"}
      key={props.voyageCode}
      className={clsx(
        styles.card,
        {
          [styles.cardDarker]:
            backgroundColor == "neutral01" || backgroundColor == "neutral05"
        },
        bgClass
      )}
    >
      <article className={styles.container}>
        <a
          onClick={_onClick}
          href={props.detailUrl}
          className={styles.container}
          data-content-piece="cruise_details"
          data-content-params={generateTrackingContentParams(props)}
          aria-label={label}
          title={label}
        >
          <div className={styles.imageContainer}>
            {props.image && (
              <ImageRatio ratio="custom">
                <ResponsiveSanityImage
                  image={props.image}
                  alt={`Cruise ${label}`}
                  defaultWidth={384}
                  defaultHeight={208}
                  aria-hidden
                />
              </ImageRatio>
            )}

            <header className={styles.headerContent}>
              <CardTags>
                {visibleDestinationName && (
                  <CardTag backgroundColor="white">
                    {visibleDestinationName}
                  </CardTag>
                )}
                {isAntarcticaBridge && (
                  <CardTag uppercase={false} backgroundColor="productType">
                    <Trans>Antarctica Bridge</Trans>
                  </CardTag>
                )}
                {props.cruiseTags.slice(0, 2).map((cruiseTag) => (
                  <CardTag
                    key={cruiseTag}
                    backgroundColor="productType"
                    uppercase={false}
                  >
                    {cruiseTag}
                  </CardTag>
                ))}
              </CardTags>

              <h3 className={styles.trip}>
                {nonBreakingSpaceCruiseName(props.departurePort?.name || "")}{" "}
                {t`port-to-port`}
                <wbr />{" "}
                {nonBreakingSpaceCruiseName(props.arrivalPort?.name || "")}
              </h3>
            </header>
          </div>
          <div className={styles.info}>
            <Dates
              departureDate={props.departureDate}
              arrivalDate={props.arrivalDate}
              durationDays={props.durationInDays}
              separator="bullet"
            />

            <div className={styles.shipInfo}>
              <div>
                <Icon
                  name="ship-lateral-stroke"
                  padding={false}
                  className={styles.shipIcon}
                  ariaHidden
                />
                {props.shipName}
              </div>
              {props.isExpedition && (
                <div>
                  <Icon
                    name="compass"
                    size="1.5x"
                    padding={false}
                    className={styles.expeditionIcon}
                    ariaHidden
                  />
                  Expedition Cruise
                </div>
              )}
            </div>
          </div>

          <footer className={styles.footer}>
            {arrayIsNotEmpty(props.promosBadge) && (
              <PromoBadges promos={props.promosBadge} variant="small" />
            )}
            <div className={styles.footerContent}>
              <div className={styles.price}>
                <LowestPrice
                  price={leadPriceFrom}
                  currency={props.currency}
                  variant="big"
                />
              </div>
              <div className={styles.cta}>
                <Button
                  renderAs="div"
                  full
                  variant="primary"
                  iconName="arrow-right"
                  size="small"
                  dataContentPiece="more_button"
                >
                  {isDesktop && (
                    <Trans id="cruis card details button label">details</Trans>
                  )}
                </Button>
              </div>
            </div>
          </footer>
        </a>
      </article>
    </ProductTypeColor>
  );
};

export default EmotionalCruiseCard;
